/**
 * Pickers and shared organisation settings (ADR-0065).
 *
 * - People search (identity `GET /v1/pickers/people`): enabled people of your own tenant by
 *   name or e-mail, optionally only holders of one role (the people who work a queue). Any
 *   signed-in person may search; `/v1/users` stays for administrators.
 * - Case types catalogue (cases `/v1/case-types`): plain bilingual label, default queue,
 *   response time in working hours and the information a case must carry.
 * - Business calendar (cases `/v1/calendar`): the organisation's single source of time
 *   settings — time zone, working days (and so the weekend), working hours and holidays — used
 *   by case SLAs, the default time zone of scheduled agent runs, and offered to channels.
 */
import type { Http } from "./http";
import type { Classification, LocalizedText, Priority } from "./types";

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------------- people

/** `Person`: what a picker needs, nothing more. */
export interface Person {
  id: string;
  display_name: string;
  username: string;
  email: string | null;
}

export interface PeopleQuery {
  q?: string;
  /** Only people holding this realm role (e.g. a task queue's role). */
  role?: string;
  limit?: number;
}

// ------------------------------------------------------------------------------ case types

export type CaseFieldKind = "text" | "number" | "date";
export const CASE_FIELD_KINDS: readonly CaseFieldKind[] = ["text", "number", "date"];

export interface CaseTypeField {
  key: string;
  label: LocalizedText;
  kind: CaseFieldKind;
  required: boolean;
}

export interface CaseTypeIn {
  label: LocalizedText;
  description?: LocalizedText | null;
  default_queue: string | null;
  /** Respond within this many working hours (business calendar). */
  response_hours: number | null;
  default_priority: Priority;
  default_classification: Classification;
  fields: CaseTypeField[];
  active: boolean;
}

export interface CaseType extends CaseTypeIn {
  key: string;
  updated_at: string;
  updated_by: string;
}

export const CASE_TYPE_KEY = /^[a-z0-9][a-z0-9_.:-]{0,63}$/;
export const CASE_FIELD_KEY = /^[a-z][a-z0-9_]{0,63}$/;

/** `invoice dispute` / `نزاع فاتورة` → a key the service accepts (`invoice_dispute`, `type_…`). */
export function slugKey(text: string, fallbackPrefix = "type"): string {
  const s = text
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
  if (s && /^[a-z]/.test(s)) return s;
  // Arabic-only (or digit-leading) names: a short, stable key from the text.
  let h = 0;
  for (const ch of text) h = (h * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  return `${fallbackPrefix}_${h.toString(36)}`;
}

/** Client-side checks (i18n keys `caseTypes:issues.<code>`); the cases service validates again. */
export function caseTypeIssues(key: string, t: CaseTypeIn): string[] {
  const issues: string[] = [];
  if (!CASE_TYPE_KEY.test(key)) issues.push("key_invalid");
  if (!t.label.en?.trim() && !t.label.ar?.trim()) issues.push("label_required");
  if (t.response_hours !== null && !(t.response_hours > 0)) issues.push("hours_invalid");
  const keys = t.fields.map((f) => f.key);
  if (keys.some((k) => !CASE_FIELD_KEY.test(k))) issues.push("field_key_invalid");
  if (new Set(keys).size !== keys.length) issues.push("field_key_duplicate");
  if (t.fields.some((f) => !f.label.en?.trim() && !f.label.ar?.trim())) issues.push("field_label_required");
  return issues;
}

const blank = (v: unknown) => v === undefined || v === null || (typeof v === "string" && !v.trim());

/** Fields a new case of this type still needs (`required` and empty), in catalogue order. */
export function missingCaseFields(t: Pick<CaseTypeIn, "fields"> | null | undefined, attrs: Record<string, unknown>): CaseTypeField[] {
  return (t?.fields ?? []).filter((f) => f.required && blank(attrs[f.key]));
}

// -------------------------------------------------------------------------------- calendar

/** ISO weekday numbers: Monday = 1 … Sunday = 7. */
export type IsoWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;
/** Display order starting on Sunday (the Saudi working week starts on Sunday). */
export const WEEK_FROM_SUNDAY: readonly IsoWeekday[] = [7, 1, 2, 3, 4, 5, 6];

export interface BusinessCalendar {
  timezone: string;
  working_days: number[];
  /** "HH:MM:SS" wall-clock times. */
  day_start: string;
  day_end: string;
  /** "YYYY-MM-DD". */
  holidays: string[];
}

export interface CalendarOut extends BusinessCalendar {
  updated_at: string | null;
  updated_by: string | null;
  weekend_days: number[];
}

export interface DuePreviewIn {
  hours: number;
  start?: string;
  calendar?: BusinessCalendar;
}

export interface DuePreviewOut {
  start: string;
  due_at: string;
  timezone: string;
}

/** Client-side calendar checks (i18n keys `calendar:issues.<code>`). */
export function calendarIssues(c: BusinessCalendar, validZones?: readonly string[]): string[] {
  const issues: string[] = [];
  if (!c.timezone.trim()) issues.push("timezone_required");
  else if (validZones && validZones.length && !validZones.includes(c.timezone)) issues.push("timezone_unknown");
  if (c.working_days.length === 0) issues.push("no_working_days");
  if (!(c.day_start < c.day_end)) issues.push("hours_order");
  return issues;
}

/** Only the fields the service accepts on PUT (drop `updated_*`, `weekend_days`). */
export function calendarBody(c: BusinessCalendar): BusinessCalendar {
  return {
    timezone: c.timezone.trim(),
    working_days: [...new Set(c.working_days)].sort((a, b) => a - b),
    day_start: c.day_start.length === 5 ? `${c.day_start}:00` : c.day_start,
    day_end: c.day_end.length === 5 ? `${c.day_end}:00` : c.day_end,
    holidays: [...new Set(c.holidays)].sort(),
  };
}

/** IANA time zones the browser knows (falls back to a short list in old engines). */
export function timeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  const zones = intl.supportedValuesOf?.("timeZone") ?? [];
  const base = zones.length ? zones : ["Asia/Riyadh", "Asia/Dubai", "Asia/Kuwait", "Asia/Qatar", "Asia/Bahrain", "Asia/Muscat", "Africa/Cairo", "Europe/London", "UTC"];
  return base.includes("UTC") ? base : [...base, "UTC"];
}

// ------------------------------------------------------------------------------------- api

export function orgSettingsApi(http: Http) {
  return {
    /** Identity: people of your own tenant for pickers. */
    people: (q: PeopleQuery = {}) => http.get<Person[]>("identity", "/v1/pickers/people", { q: q.q, role: q.role, limit: q.limit ?? 20 }),
    caseTypes: (includeInactive = false) => http.get<CaseType[]>("cases", "/v1/case-types", { include_inactive: includeInactive || undefined }),
    putCaseType: (key: string, body: CaseTypeIn) => http.put<CaseType>("cases", `/v1/case-types/${enc(key)}`, body),
    deleteCaseType: (key: string) => http.delete<void>("cases", `/v1/case-types/${enc(key)}`),
    calendar: () => http.get<CalendarOut>("cases", "/v1/calendar"),
    putCalendar: (body: BusinessCalendar) => http.put<CalendarOut>("cases", "/v1/calendar", calendarBody(body)),
    previewDue: (body: DuePreviewIn) => http.post<DuePreviewOut>("cases", "/v1/calendar/preview", body),
  };
}

export type OrgSettingsApi = ReturnType<typeof orgSettingsApi>;
